import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Search, Plus, X, Trash2, ArrowUpDown } from "lucide-react";
import {
  useFireRiskProcessStore,
  useFireRiskSubProcessStore,
  useHazardousMaterialRiskProcessStore,
  useHazardousMaterialRiskSubProcessStore,
  useOrgUsersStore,
  useRiskAssessment,
} from "../../../store/store";
import Tooltip from "@mui/material/Tooltip";
import { OrgUserType } from "../../../types/api";
import { MultiSelectDropdown } from "../../../components/dropdown/MultiSelectDropdown";
import "./CreationTable.css";

interface tableFormat {
  header: { title: string; colnumber: number }[];
  columns: {
    title: string;
    type: string;
    columnName: string;
    classnames?: string;
    isDisabled?: boolean;
    options?: any[];
  }[];
  emptyObject: Record<string, any>;
}

interface EditableTableProps {
  initialData: any[];
  structure: tableFormat;
  type: "HWR" | "AWR";
  isReadOnly?: boolean;
  moduleName?: string;
}

export interface CreationTableHandle {
  checkBeforeNext: () => boolean;
}

export const CreationTable = forwardRef<
  CreationTableHandle,
  EditableTableProps
>(({ initialData, structure, type, isReadOnly, moduleName }, ref) => {
  const {
    riskAssessment,
    updateHwr,
    createHwr,
    createAsr,
    updateAsr,
    deleteHwr,
    deleteAsr,
  } = useRiskAssessment();

  // Stores
  const { fireRiskProcessessData, fetchFireRiskProcesses } = useFireRiskProcessStore();
  const { fireRiskSubProcessData, fetchFireRiskSubProcesses } = useFireRiskSubProcessStore();
  const {
    hazardousMaterialRiskProcessessData,
    fetchHazardousMaterialRiskProcesses,
  } = useHazardousMaterialRiskProcessStore();
  const {
    hazardousMaterialRiskSubProcessData,
    fetchHazardousMaterialRiskSubProcesses,
  } = useHazardousMaterialRiskSubProcessStore();
  const { orgUsersData } = useOrgUsersStore();

  // Data & View state
  const [data, setData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("init");
  const [sortDir, setSortDir] = useState<-1 | 1>(-1);

  // Editor state
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRowIdx, setSelectedRowIdx] = useState(-1);
  const [formObject, setFormObject] = useState<Record<string, any>>(() =>
    JSON.parse(JSON.stringify(structure.emptyObject))
  );
  const [saveErr, setSaveErr] = useState("");
  const [missingCols, setMissingCols] = useState<string[]>([]);
  const [newRowErr, setNewRowErr] = useState("");

  // Fields that must be filled before the user can move to the next step.
  // Excludes auto-calculated totals and the optional re-assessment group
  // (those only matter once "Solution implemented" is toggled on).
  const requiredFields = structure.columns
    .filter(
      (col) =>
        !col.isDisabled &&
        !col.columnName.startsWith("recalculationOfRisk.") &&
        col.columnName !== "remarks"
    )
    .map((col) => ({
      columnName: col.columnName,
      type: col.type,
      label: col.title,
    }));

  const getColValue = (columnName: string, obj: Record<string, any>): any =>
    columnName
      .split(".")
      .reduce((acc: any, key: string) => (acc == null ? acc : acc[key]), obj);

  const errClass = (columnName: string): string =>
    missingCols.includes(columnName) ? "field-error" : "";

  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  useEffect(() => {
    if (moduleName === "hazardous-materials" || moduleName === "hazardousMaterials") {
      fetchHazardousMaterialRiskProcesses();
      fetchHazardousMaterialRiskSubProcesses();
    } else {
      fetchFireRiskProcesses();
      fetchFireRiskSubProcesses();
    }
  }, [moduleName]);

  // Helpers to fetch readable names
  const getProcessNameById = (id: string) => {
    if (moduleName === "hazardous-materials" || moduleName === "hazardousMaterials") {
      const found = (hazardousMaterialRiskProcessessData as any[]).find(
        (p: any) => p.id === id || p._id === id
      );
      if (found) return found.hazardousMaterialRiskProcessName || found.name;
    } else {
      const found = (fireRiskProcessessData as any[]).find(
        (p: any) => p.id === id || p._id === id
      );
      if (found) return found.fireRiskProcessName || found.name;
    }

    const colOpt = structure.columns.find((c) => c.columnName === "process")?.options;
    if (Array.isArray(colOpt)) {
      const found = colOpt.find((p: any) => p.id === id || p._id === id);
      if (found) {
        return (
          found.fireRiskProcessName ||
          found.hazardousMaterialRiskProcessName ||
          found.name ||
          id
        );
      }
    }
    return id || "—";
  };

  const getSubProcessNameById = (id: string) => {
    if (moduleName === "hazardous-materials" || moduleName === "hazardousMaterials") {
      const found = (hazardousMaterialRiskSubProcessData as any[]).find(
        (sp: any) => sp.id === id || sp._id === id
      );
      if (found) return found.hazardousMaterialRiskSubProcessName || found.name;
    } else {
      const found = (fireRiskSubProcessData as any[]).find(
        (sp: any) => sp.id === id || sp._id === id
      );
      if (found) return found.fireRiskSubProcessName || found.name;
    }

    const colOpt = structure.columns.find((c) => c.columnName === "subProcesses")?.options;
    if (Array.isArray(colOpt)) {
      const found = colOpt.find((sp: any) => sp.id === id || sp._id === id);
      if (found) {
        return (
          found.fireRiskSubProcessName ||
          found.hazardousMaterialRiskSubProcessName ||
          found.name ||
          id
        );
      }
    }
    return id || "—";
  };

  const getPremiseUsersById = (id: string) => {
    const user = orgUsersData.find((u: OrgUserType) => u._id === id);
    return user ? `${user.firstName} ${user.lastName}` : id;
  };

  // Severity scoring colors mapping
  const getBand = (score: number) => {
    if (score >= 300) {
      return { text: "High", bg: "var(--high)", cb: "var(--high-bg)", c: "var(--high)" };
    } else if (score >= 125) {
      return { text: "Medium", bg: "var(--med)", cb: "var(--med-bg)", c: "var(--med)" };
    } else {
      return { text: "Low", bg: "var(--low)", cb: "var(--low-bg)", c: "var(--low)" };
    }
  };

  const getScoreColorClass = (value: number): string => {
    if (moduleName === "external-emergency-preparedness") {
      if (value >= 1 && value <= 2) return "bg-green-100 text-green-700";
      if (value >= 3 && value <= 4) return "bg-orange-100 text-orange-700";
      return "bg-red-100 text-red-700";
    }
    if (value >= 1 && value <= 5) return "bg-green-100 text-green-700";
    if (value >= 6 && value <= 7) return "bg-orange-100 text-orange-700";
    return "bg-red-100 text-red-700";
  };

  // Dynamic nested value updator
  const updateNestedValue = (
    obj: Record<string, any>,
    path: string,
    value: any,
    fieldType: string
  ): Record<string, any> => {
    const keys = path.split(".");
    const newObj = { ...obj };
    let current = newObj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      current[key] = { ...current[key] };
      current = current[key];
    }

    const lastKey = keys[keys.length - 1];
    current[lastKey] =
      fieldType === "multiselect"
        ? value
        : fieldType === "number"
          ? Number(value)
          : value;
    return newObj;
  };

  const handleFieldChange = (path: string, value: any, fieldType: string) => {
    setSaveErr("");
    setNewRowErr("");
    setMissingCols((prev) => prev.filter((col) => col !== path));
    const maxScore = moduleName === "external-emergency-preparedness" ? 5 : 10;

    // Numerical values validation
    if (fieldType === "number" && !path.includes("TimeLineToImplement")) {
      const num = Number(value);
      if (num > maxScore || num < 1) {
        return; // strictly ignore out of boundary input
      }
    }

    let updated = updateNestedValue(formObject, path, value, fieldType);

    if (path === "process") {
      updated = updateNestedValue(updated, "subProcesses", "", "string");
    }

    // Dynamic RPN Calculations
    const initP = Number(updated.initialActualRisk?.ProbabilityOfFailure || 0);
    const initS = Number(updated.initialActualRisk?.SeverityOfOutCome || 0);
    const initD = Number(updated.initialActualRisk?.AbilityToDetectFailure || 0);
    updated.initialActualRisk.TotalRiskScore = initP * initS * initD;

    const reP = Number(updated.recalculationOfRisk?.ProbabilityOfFailure || 0);
    const reS = Number(updated.recalculationOfRisk?.SeverityOfOutCome || 0);
    const reD = Number(updated.recalculationOfRisk?.AbilityToDetectFailure || 0);
    updated.recalculationOfRisk.TotalRiskScore = reP * reS * reD;

    setFormObject(updated);
  };

  // CRUD actions
  const handleOpenAdd = () => {
    setFormObject(JSON.parse(JSON.stringify(structure.emptyObject)));
    setSelectedRowIdx(-1);
    setSaveErr("");
    setNewRowErr("");
    setMissingCols([]);
    setIsEditing(true);
  };

  const handleOpenEdit = (rowData: any, idx: number) => {
    if (isReadOnly) return;
    setFormObject(JSON.parse(JSON.stringify(rowData)));
    setSelectedRowIdx(idx);
    setSaveErr("");
    setNewRowErr("");
    setMissingCols([]);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedRowIdx(-1);
    setFormObject(JSON.parse(JSON.stringify(structure.emptyObject)));
    setNewRowErr("");
    setMissingCols([]);
  };

  const handleSave = async () => {
    if (!formObject.process) {
      setSaveErr("Please select a process");
      return;
    }
    const copyForm: any = { ...formObject, assessment: riskAssessment?._id };

    try {
      if (selectedRowIdx >= 0) {
        if (type === "HWR") {
          await updateHwr(copyForm, moduleName);
        } else {
          await updateAsr(copyForm, moduleName);
        }
      } else {
        if (type === "HWR") {
          await createHwr(copyForm, moduleName);
        } else {
          await createAsr(copyForm, moduleName);
        }
      }
      setIsEditing(false);
      setSelectedRowIdx(-1);
      setFormObject(JSON.parse(JSON.stringify(structure.emptyObject)));
      setMissingCols([]);
      setNewRowErr("");
    } catch (err: any) {
      setSaveErr(err.message || "Failed to save risk assessment row.");
    }
  };

  const deleteRow = async (_id: string) => {
    if (isReadOnly || riskAssessment.status !== "open") return;
    try {
      if (type === "HWR") {
        await deleteHwr(_id);
      } else {
        await deleteAsr(_id);
      }
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 1 ? -1 : 1));
    } else {
      setSortField(field);
      setSortDir(-1); // Default desc
    }
  };

  // Tooltip detailed breakdown
  const RpnTooltipContent = ({
    kind,
    p,
    s,
    d,
    score,
  }: {
    kind: string;
    p: number;
    s: number;
    d: number;
    score: number;
  }) => {
    const b = getBand(score);
    return (
      <div className="custom-tip-card">
        <div className="th">
          <b>{kind} risk score</b>
          <span className="pill" style={{ backgroundColor: b.bg }}>
            {b.text}
          </span>
        </div>
        <div className="fac">
          <span>Probability</span>
          <b>{p}</b>
        </div>
        <div className="fac">
          <span>Severity</span>
          <b>{s}</b>
        </div>
        <div className="fac">
          <span>Detectability</span>
          <b>{d}</b>
        </div>
        <div className="eq">
          <span>
            {p} × {s} × {d}
          </span>
          <span className="res">{score}</span>
        </div>
      </div>
    );
  };

  // Filtering data
  const filteredData = data.filter((row) => {
    const procName = getProcessNameById(row.process).toLowerCase();
    const subName = getSubProcessNameById(row.subProcesses).toLowerCase();
    const failMode = (row.possibleFailureRisk || "").toLowerCase();
    const query = searchQuery.toLowerCase();

    const matchesSearch =
      procName.includes(query) || subName.includes(query) || failMode.includes(query);

    const isMit =
      row.recalculationOfRisk?.SolutionImplemented === "Yes fully" ||
      row.recalculationOfRisk?.SolutionImplemented === "Partially";

    if (statusFilter === "open") return matchesSearch && !isMit;
    if (statusFilter === "mit") return matchesSearch && isMit;
    if (statusFilter === "high") {
      const initScore = Number(row.initialActualRisk?.TotalRiskScore || 0);
      return matchesSearch && initScore >= 300;
    }
    return matchesSearch;
  });

  // Sorting data
  const sortedData = [...filteredData].sort((a, b) => {
    let valA: any = "";
    let valB: any = "";

    switch (sortField) {
      case "proc":
        valA = getProcessNameById(a.process);
        valB = getProcessNameById(b.process);
        break;
      case "mode":
        valA = a.possibleFailureRisk || "";
        valB = b.possibleFailureRisk || "";
        break;
      case "action":
        valA = a.actionsSolution || "";
        valB = b.actionsSolution || "";
        break;
      case "owner":
        valA = Array.isArray(a.responsibilityIndividual)
          ? a.responsibilityIndividual.map((id: string) => getPremiseUsersById(id)).join(", ")
          : "";
        valB = Array.isArray(b.responsibilityIndividual)
          ? b.responsibilityIndividual.map((id: string) => getPremiseUsersById(id)).join(", ")
          : "";
        break;
      case "time":
        valA = Number(a.TimeLineToImplement) || 0;
        valB = Number(b.TimeLineToImplement) || 0;
        break;
      case "init":
        valA = Number(a.initialActualRisk?.TotalRiskScore) || 0;
        valB = Number(b.initialActualRisk?.TotalRiskScore) || 0;
        break;
      case "res":
        valA = Number(a.recalculationOfRisk?.TotalRiskScore) || 0;
        valB = Number(b.recalculationOfRisk?.TotalRiskScore) || 0;
        break;
      case "status":
        valA =
          a.recalculationOfRisk?.SolutionImplemented === "Yes fully" ||
            a.recalculationOfRisk?.SolutionImplemented === "Partially"
            ? 1
            : 0;
        valB =
          b.recalculationOfRisk?.SolutionImplemented === "Yes fully" ||
            b.recalculationOfRisk?.SolutionImplemented === "Partially"
            ? 1
            : 0;
        break;
    }

    if (typeof valA === "string") {
      return valA.localeCompare(valB) * sortDir;
    }
    return (valA - valB) * sortDir;
  });

  // Fetch lists for editing select
  const processOptions = (structure.columns.find((c) => c.columnName === "process")?.options ||
    (moduleName === "hazardous-materials" || moduleName === "hazardousMaterials"
      ? hazardousMaterialRiskProcessessData
      : fireRiskProcessessData)) as any[];

  const subProcessOptions = ((structure.columns.find((c) => c.columnName === "subProcesses")?.options ||
    (moduleName === "hazardous-materials" || moduleName === "hazardousMaterials"
      ? hazardousMaterialRiskSubProcessData
      : fireRiskSubProcessData)) as any[]).filter(
        (sp: any) =>
          (sp.fireRiskProcessId || sp.hazardousMaterialRiskProcessId || sp.processId) ===
          formObject.process
      );

  const ownerOptions = orgUsersData.map((opt: any) => ({
    label: `${opt.firstName} ${opt.lastName}`,
    value: opt._id,
  }));

  const initialRPN = Number(formObject.initialActualRisk?.TotalRiskScore || 0);
  const initialBand = getBand(initialRPN);

  const residualRPN = Number(formObject.recalculationOfRisk?.TotalRiskScore || 0);
  const residualBand = getBand(residualRPN);

  const isSolutionImplemented =
    formObject.recalculationOfRisk?.SolutionImplemented === "Yes fully" ||
    formObject.recalculationOfRisk?.SolutionImplemented === "Partially";

  const handleEditorKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
  };

  // Validation invoked by the parent's "Next" button.
  // Returns true when it's safe to proceed, false (and shows a prompt) otherwise.
  useImperativeHandle(ref, () => ({
    checkBeforeNext: () => {
      if (isReadOnly || riskAssessment.status !== "open") return true;

      const missing = requiredFields.filter(({ columnName, type: colType }) => {
        const value = getColValue(columnName, formObject);
        if (
          value === undefined ||
          value === null ||
          String(value).trim() === ""
        )
          return true;
        if (colType === "number" && Number(value) <= 0) return true;
        return false;
      });
      const filledCount = requiredFields.length - missing.length;
      const isRowDirty = filledCount > 0;
      const hasRows = (data?.length || 0) > 0;

      // A row has been started but some required fields are still empty
      if (isRowDirty && missing.length > 0) {
        setMissingCols(missing.map((f) => f.columnName));
        setNewRowErr(
          `Please fill the required field(s): ${missing
            .map((f) => f.label)
            .join(", ")}`,
        );
        return false;
      }

      // A row is fully filled but not yet added to the table
      if (isRowDirty && missing.length === 0) {
        setNewRowErr('Please click "Save" to add this row before proceeding.');
        return false;
      }

      // Nothing entered and no rows added yet
      if (!hasRows) {
        setMissingCols(requiredFields.map((f) => f.columnName));
        setNewRowErr(
          'Please fill the required fields and click "Save" to add at least one row before proceeding.',
        );
        return false;
      }

      setMissingCols([]);
      setNewRowErr("");
      return true;
    },
  }));

  return (
    <div className="risk-step">
      {newRowErr && <div className="new-row-err">{newRowErr}</div>}
      {/* TOOLBAR */}
      <div className="risk-tools">
        <h2>{type === "HWR" ? "Hospital wide risks" : "Area wide risks"}</h2>
        <div className="grow" />
        <div className="search">
          <svg viewBox="0 0 24 24" strokeWidth="2">
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4-4" />
          </svg>
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="chips">
          <div
            className={`chip ${statusFilter === "all" ? "on" : ""}`}
            onClick={() => setStatusFilter("all")}
          >
            All
          </div>
          <div
            className={`chip ${statusFilter === "open" ? "on" : ""}`}
            onClick={() => setStatusFilter("open")}
          >
            Open
          </div>
          <div
            className={`chip ${statusFilter === "mit" ? "on" : ""}`}
            onClick={() => setStatusFilter("mit")}
          >
            Mitigated
          </div>
          <div
            className={`chip ${statusFilter === "high" ? "on" : ""}`}
            onClick={() => setStatusFilter("high")}
          >
            High risk
          </div>
        </div>
        {!isReadOnly && riskAssessment.status === "open" && (
          <button className="addbtn" onClick={handleOpenAdd}>
            <Plus className="inline-block w-4 h-4 mr-1 -mt-0.5" /> Add risk
          </button>
        )}
      </div>

      {/* RISK BODY / SPLIT LAYOUT */}
      <div className={`risk-body ${isEditing ? "editing" : ""}`}>
        {/* TABLE SECTION */}
        <div className="tablewrap">
          <table className="risk-table">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("proc")}
                  className={sortField === "proc" ? "sorted" : ""}
                >
                  Risk area <ArrowUpDown className="arr inline-block w-3 h-3" />
                </th>
                <th
                  onClick={() => handleSort("mode")}
                  className={sortField === "mode" ? "sorted" : ""}
                >
                  Failure mode <ArrowUpDown className="arr inline-block w-3 h-3" />
                </th>
                <th
                  onClick={() => handleSort("action")}
                  className={sortField === "action" ? "sorted" : ""}
                >
                  Action / solution <ArrowUpDown className="arr inline-block w-3 h-3" />
                </th>
                <th
                  onClick={() => handleSort("owner")}
                  className={sortField === "owner" ? "sorted" : ""}
                >
                  Owner <ArrowUpDown className="arr inline-block w-3 h-3" />
                </th>
                <th
                  onClick={() => handleSort("time")}
                  className={sortField === "time" ? "sorted" : ""}
                >
                  Timeline <ArrowUpDown className="arr inline-block w-3 h-3" />
                </th>
                <th
                  onClick={() => handleSort("init")}
                  className={`num ${sortField === "init" ? "sorted" : ""}`}
                >
                  Initial <ArrowUpDown className="arr inline-block w-3 h-3" />
                </th>
                <th
                  onClick={() => handleSort("res")}
                  className={`num ${sortField === "res" ? "sorted" : ""}`}
                >
                  Residual <ArrowUpDown className="arr inline-block w-3 h-3" />
                </th>
                <th
                  onClick={() => handleSort("status")}
                  className={sortField === "status" ? "sorted" : ""}
                >
                  Status <ArrowUpDown className="arr inline-block w-3 h-3" />
                </th>
                {!isReadOnly && riskAssessment.status === "open" && <th />}
              </tr>
            </thead>
            <tbody>
              {sortedData.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <div className="empty">
                      No risks recorded. Click <b>+ Add risk</b> to begin.
                    </div>
                  </td>
                </tr>
              ) : (
                sortedData.map((row, idx) => {
                  const initVal = Number(row.initialActualRisk?.TotalRiskScore || 0);
                  const initB = getBand(initVal);

                  const hasRes =
                    row.recalculationOfRisk?.SolutionImplemented === "Yes fully" ||
                    row.recalculationOfRisk?.SolutionImplemented === "Partially";
                  const resVal = hasRes ? Number(row.recalculationOfRisk?.TotalRiskScore || 0) : null;
                  const resB = resVal !== null ? getBand(resVal) : null;

                  const isMitigated = hasRes;

                  return (
                    <tr
                      key={row._id || idx}
                      className={selectedRowIdx === idx ? "active" : ""}
                      onClick={() => handleOpenEdit(row, idx)}
                    >
                      <td>
                        <div className="proc">{getProcessNameById(row.process)}</div>
                        <div className="sub">{getSubProcessNameById(row.subProcesses)}</div>
                      </td>
                      <td>
                        <div className="mode" title={row.possibleFailureRisk}>
                          {row.possibleFailureRisk || "—"}
                        </div>
                      </td>
                      <td>
                        <div className="mode" title={row.actionsSolution}>
                          {row.actionsSolution || <span className="dash">—</span>}
                        </div>
                      </td>
                      <td>
                        <span className="owner">
                          {Array.isArray(row.responsibilityIndividual)
                            ? row.responsibilityIndividual
                              .map((id: string) => getPremiseUsersById(id))
                              .join(", ")
                            : "—"}
                        </span>
                      </td>
                      <td>
                        <span className="owner">
                          {row.TimeLineToImplement
                            ? `${row.TimeLineToImplement} d`
                            : <span className="dash">—</span>}
                        </span>
                      </td>
                      <td className="num">
                        <Tooltip
                          title={
                            <RpnTooltipContent
                              kind="Initial"
                              p={Number(row.initialActualRisk?.ProbabilityOfFailure || 0)}
                              s={Number(row.initialActualRisk?.SeverityOfOutCome || 0)}
                              d={Number(row.initialActualRisk?.AbilityToDetectFailure || 0)}
                              score={initVal}
                            />
                          }
                          arrow
                          placement="top"
                        >
                          <span
                            className="chipnum"
                            style={{ backgroundColor: initB.cb, color: initB.c }}
                          >
                            {initVal}
                          </span>
                        </Tooltip>
                      </td>
                      <td className="num">
                        {resVal !== null && resB ? (
                          <Tooltip
                            title={
                              <RpnTooltipContent
                                kind="Residual"
                                p={Number(row.recalculationOfRisk?.ProbabilityOfFailure || 0)}
                                s={Number(row.recalculationOfRisk?.SeverityOfOutCome || 0)}
                                d={Number(row.recalculationOfRisk?.AbilityToDetectFailure || 0)}
                                score={resVal}
                              />
                            }
                            arrow
                            placement="top"
                          >
                            <span
                              className="chipnum"
                              style={{ backgroundColor: resB.cb, color: resB.c }}
                            >
                              {resVal}
                            </span>
                          </Tooltip>
                        ) : (
                          <span className="dash">—</span>
                        )}
                      </td>
                      <td>
                        {isMitigated ? (
                          <span className="status" style={{ color: "var(--low)" }}>
                            <span className="dot" style={{ backgroundColor: "var(--low)" }} />
                            Mitigated
                          </span>
                        ) : (
                          <span className="status" style={{ color: "var(--med)" }}>
                            <span className="dot" style={{ backgroundColor: "var(--med)" }} />
                            Open
                          </span>
                        )}
                      </td>
                      {!isReadOnly && riskAssessment.status === "open" && (
                        <td
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm("Delete this risk assessment row?")) {
                              deleteRow(row._id);
                            }
                          }}
                        >
                          <Trash2 className="text-red-500 hover:text-red-700 w-4 h-4 cursor-pointer" />
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* EDITOR SECTION */}
        {isEditing && !isReadOnly && (
          <div className="editor" onKeyDown={handleEditorKeyDown}>
            <div className="ed-head">
              <div className="crumb">
                Editing
                <b>
                  {selectedRowIdx >= 0
                    ? `${getProcessNameById(formObject.process)} · ${getSubProcessNameById(
                      formObject.subProcesses
                    )}`
                    : "New risk"}
                </b>
              </div>
              <button className="ed-x" onClick={handleCancel}>
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="ed-body">
              {saveErr && <p className="text-red-600 mb-3 text-xs font-semibold">{saveErr}</p>}

              {/* PHASE 1: IDENTIFY */}
              <div className="phase">
                <div className="phase-label">
                  <span className="pn">01</span>
                  <h3>Identify</h3>
                  <span className="rule" />
                </div>
                <div className="grid two">
                  <div>
                    <label>Process</label>
                    <select
                      className={errClass("process")}
                      value={formObject.process || ""}
                      disabled={isReadOnly}
                      onChange={(e) => handleFieldChange("process", e.target.value, "string")}
                    >
                      <option value="">Select process...</option>
                      {processOptions.map((op: any) => (
                        <option key={op._id || op.id} value={op._id || op.id}>
                          {op.fireRiskProcessName ||
                            op.hazardousMaterialRiskProcessName ||
                            op.name ||
                            op.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label>Sub-process</label>
                    <select
                      className={errClass("subProcesses")}
                      value={formObject.subProcesses || ""}
                      onChange={(e) => handleFieldChange("subProcesses", e.target.value, "string")}
                      disabled={isReadOnly || !formObject.process}
                    >
                      <option value="">
                        {!formObject.process ? "Select process first..." : "Select sub-process..."}
                      </option>
                      {subProcessOptions.map((op: any) => (
                        <option key={op._id || op.id} value={op._id || op.id}>
                          {op.fireRiskSubProcessName ||
                            op.hazardousMaterialRiskSubProcessName ||
                            op.name ||
                            op.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="full">
                    <label>Possible failure mode</label>
                    <input
                      type="text"
                      className={errClass("possibleFailureRisk")}
                      placeholder="What could go wrong"
                      value={formObject.possibleFailureRisk || ""}
                      disabled={isReadOnly}
                      onChange={(e) =>
                        handleFieldChange("possibleFailureRisk", e.target.value, "string")
                      }
                    />
                  </div>
                  <div className="full">
                    <label>Objective</label>
                    <input
                      type="text"
                      className={errClass("objective")}
                      placeholder="What this step should achieve"
                      value={formObject.objective || ""}
                      onChange={(e) => {
                        if (isReadOnly) return;
                        handleFieldChange("objective", e.target.value, "string")
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* PHASE 2: ASSESS */}
              <div className="phase">
                <div className="phase-label">
                  <span className="pn">02</span>
                  <h3>Assess</h3>
                  <span className="rule" />
                </div>
                <div className="scores">
                  <div className="sc">
                    <label>Probability</label>
                    <input
                      type="number"
                      min={1}
                      max={moduleName === "external-emergency-preparedness" ? 5 : 10}
                      className={`num ${errClass("initialActualRisk.ProbabilityOfFailure")}`}
                      disabled={isReadOnly}
                      data-lvl={
                        Number(formObject.initialActualRisk?.ProbabilityOfFailure) <= 3
                          ? "low"
                          : Number(formObject.initialActualRisk?.ProbabilityOfFailure) <= 6
                            ? "mid"
                            : "high"
                      }
                      value={formObject.initialActualRisk?.ProbabilityOfFailure || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "initialActualRisk.ProbabilityOfFailure",
                          e.target.value,
                          "number"
                        )
                      }
                    />
                  </div>
                  <div className="sc">
                    <label>Severity</label>
                    <input
                      type="number"
                      min={1}
                      max={moduleName === "external-emergency-preparedness" ? 5 : 10}
                      className={`num ${errClass("initialActualRisk.SeverityOfOutCome")}`}
                      data-lvl={
                        Number(formObject.initialActualRisk?.SeverityOfOutCome) <= 3
                          ? "low"
                          : Number(formObject.initialActualRisk?.SeverityOfOutCome) <= 6
                            ? "mid"
                            : "high"
                      }
                      value={formObject.initialActualRisk?.SeverityOfOutCome || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "initialActualRisk.SeverityOfOutCome",
                          e.target.value,
                          "number"
                        )
                      }
                    />
                  </div>
                  <div className="sc">
                    <label>Detectability</label>
                    <input
                      type="number"
                      min={1}
                      max={moduleName === "external-emergency-preparedness" ? 5 : 10}
                      className={`num ${errClass("initialActualRisk.AbilityToDetectFailure")}`}
                      data-lvl={
                        Number(formObject.initialActualRisk?.AbilityToDetectFailure) <= 3
                          ? "low"
                          : Number(formObject.initialActualRisk?.AbilityToDetectFailure) <= 6
                            ? "mid"
                            : "high"
                      }
                      value={formObject.initialActualRisk?.AbilityToDetectFailure || ""}
                      onChange={(e) =>
                        handleFieldChange(
                          "initialActualRisk.AbilityToDetectFailure",
                          e.target.value,
                          "number"
                        )
                      }
                    />
                  </div>
                </div>
                <div className="rpnbar" style={{ backgroundColor: initialBand.bg }}>
                  <span className="k">Risk score</span>
                  <span>
                    <span className="v">{initialRPN}</span> · {initialBand.text}
                  </span>
                </div>
              </div>

              {/* PHASE 3: MITIGATE */}
              <div className="phase">
                <div className="phase-label">
                  <span className="pn">03</span>
                  <h3>Mitigate</h3>
                  <span className="rule" />
                </div>
                <div className="grid two">
                  <div className="full">
                    <label>Action / solution</label>
                    <textarea
                      className={errClass("actionsSolution")}
                      placeholder="Corrective action"
                      value={formObject.actionsSolution || ""}
                      disabled={isReadOnly}
                      onChange={(e) =>
                        handleFieldChange("actionsSolution", e.target.value, "string")
                      }
                    />
                  </div>
                  <div className="full">
                    <label>Owner</label>
                    <MultiSelectDropdown
                      options={ownerOptions}
                      selectedValues={
                        Array.isArray(formObject.responsibilityIndividual)
                          ? formObject.responsibilityIndividual
                          : []
                      }
                      onChange={(values) => {
                        if (isReadOnly) return;
                        handleFieldChange("responsibilityIndividual", values, "multiselect")
                      }}
                      placeholder="Assign..."
                      containerClassName={`bg-white border rounded ${errClass("responsibilityIndividual")}`}
                    />
                  </div>
                  <div>
                    <label>
                      Timeline <span className="h">(days)</span>
                    </label>
                    <input
                      type="number"
                      className={errClass("TimeLineToImplement")}
                      placeholder="30"
                      value={formObject.TimeLineToImplement || ""}
                      onChange={(e) => {
                        if (isReadOnly) return;
                        handleFieldChange("TimeLineToImplement", e.target.value, "number")
                      }}
                    />
                  </div>
                  <div className="full">
                    <label>Monitoring procedure</label>
                    <input
                      type="text"
                      className={errClass("monitoringProcedure")}
                      placeholder="How sustenance is tracked"
                      value={formObject.monitoringProcedure || ""}
                      onChange={(e) =>
                        handleFieldChange("monitoringProcedure", e.target.value, "string")
                      }
                    />
                  </div>
                </div>
              </div>

              {/* PHASE 4: RE-ASSESS */}
              <div className="phase">
                <div className="phase-label">
                  <span className="pn">04</span>
                  <h3>Re-assess</h3>
                  <span className="rule" />
                </div>
                <div className={`gate ${isSolutionImplemented ? "on" : ""}`}>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={isSolutionImplemented}
                      disabled={isReadOnly}
                      onChange={(e) => {
                        handleFieldChange(
                          "recalculationOfRisk.SolutionImplemented",
                          e.target.checked ? "Yes fully" : "Not Implemented",
                          "string"
                        );
                      }}
                    />
                    <span className="sl" />
                  </label>
                  <div>
                    <b>Solution implemented?</b>
                    <p>Recalculation unlocks once the fix is in place.</p>
                  </div>
                </div>

                {isSolutionImplemented && (
                  <div className="reassess" style={{ marginTop: "11px" }}>
                    <div className="scores">
                      <div className="sc">
                        <label>Probability</label>
                        <input
                          type="number"
                          min={1}
                          max={moduleName === "external-emergency-preparedness" ? 5 : 10}
                          className="num"
                          data-lvl={
                            Number(formObject.recalculationOfRisk?.ProbabilityOfFailure) <= 3
                              ? "low"
                              : Number(formObject.recalculationOfRisk?.ProbabilityOfFailure) <= 6
                                ? "mid"
                                : "high"
                          }
                          value={formObject.recalculationOfRisk?.ProbabilityOfFailure || ""}
                          onChange={(e) =>
                            handleFieldChange(
                              "recalculationOfRisk.ProbabilityOfFailure",
                              e.target.value,
                              "number"
                            )
                          }
                        />
                      </div>
                      <div className="sc">
                        <label>Severity</label>
                        <input
                          type="number"
                          min={1}
                          max={moduleName === "external-emergency-preparedness" ? 5 : 10}
                          className="num"
                          data-lvl={
                            Number(formObject.recalculationOfRisk?.SeverityOfOutCome) <= 3
                              ? "low"
                              : Number(formObject.recalculationOfRisk?.SeverityOfOutCome) <= 6
                                ? "mid"
                                : "high"
                          }
                          value={formObject.recalculationOfRisk?.SeverityOfOutCome || ""}
                          onChange={(e) =>
                            handleFieldChange(
                              "recalculationOfRisk.SeverityOfOutCome",
                              e.target.value,
                              "number"
                            )
                          }
                        />
                      </div>
                      <div className="sc">
                        <label>Detectability</label>
                        <input
                          type="number"
                          min={1}
                          max={moduleName === "external-emergency-preparedness" ? 5 : 10}
                          className="num"
                          data-lvl={
                            Number(formObject.recalculationOfRisk?.AbilityToDetectFailure) <= 3
                              ? "low"
                              : Number(formObject.recalculationOfRisk?.AbilityToDetectFailure) <= 6
                                ? "mid"
                                : "high"
                          }
                          value={formObject.recalculationOfRisk?.AbilityToDetectFailure || ""}
                          onChange={(e) =>
                            handleFieldChange(
                              "recalculationOfRisk.AbilityToDetectFailure",
                              e.target.value,
                              "number"
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className="rpnbar" style={{ backgroundColor: residualBand.bg }}>
                      <span className="k">Residual</span>
                      <span>
                        <span className="v">{residualRPN}</span> · {residualBand.text}
                      </span>
                    </div>

                    <div className="grid two" style={{ marginTop: "11px" }}>
                      <div>
                        <label>Next steps</label>
                        <input
                          type="text"
                          placeholder="Ongoing controls"
                          value={formObject.recalculationOfRisk?.nextStepOnSustenance || ""}
                          onChange={(e) =>
                            handleFieldChange(
                              "recalculationOfRisk.nextStepOnSustenance",
                              e.target.value,
                              "string"
                            )
                          }
                        />
                      </div>
                      <div>
                        <label>Remarks</label>
                        <input
                          type="text"
                          placeholder="Notes"
                          value={formObject.remarks || ""}
                          onChange={(e) => handleFieldChange("remarks", e.target.value, "string")}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="ed-foot">
              <span className="kbd-hint">Tab to move · Ctrl+Enter to save</span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button className="b ghost" onClick={handleCancel}>
                  Cancel
                </button>
                <button className="b save" onClick={handleSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

CreationTable.displayName = "CreationTable";
